/**
 * "Why?" next to a reason on the case: a small button that opens a pop-up with the rule
 * explained (what it checks, what to do) and links to the full explanation and to the
 * guide of the case's country.
 *
 * The reason carries a registry rule id (`gate.competitor`) and the company (`uk`). The
 * pop-up shows the speciality of that country when there is one, else the rule for every
 * country. A rule id without an explanation renders nothing, never a broken link.
 */

import { Button, Popover, Typography } from "@norican/react";
import { Link } from "react-router";

import { logger } from "@/lib/logger";
import type { RuleSeverity } from "@/lib/rules";
import { track } from "@/lib/usage";

import {
  explanationHref,
  type SeenTone,
  toneIn,
  type WhyExplanation,
  whyFor,
  whyForReason,
} from "./content";
import { ToneBadge } from "./rule-card";

const LINK = "text-accent-fg-subtle font-semibold hover:underline";

const SEVERITY_TONE: Record<RuleSeverity, SeenTone> = {
  blocking: "blocked",
  warning: "warning",
  info: "info",
};

/**
 * What this reason is on the case. The reason's own severity when known (a card can
 * explain a block and a warning together, like expired and old prices), else the mode of
 * this one rule for the company.
 */
function toneOf(why: WhyExplanation, severity?: RuleSeverity | null): SeenTone {
  if (severity && Object.hasOwn(SEVERITY_TONE, severity)) {
    return SEVERITY_TONE[severity];
  }
  const { primary, entity, ruleId } = why;
  return entity ? toneIn(primary.rule, [entity], [ruleId]) : primary.rule.tone;
}

/** The body of the pop-up (exported for the tests: a closed pop-up renders no body). */
export function WhyContent({
  why,
  severity,
}: {
  why: WhyExplanation;
  severity?: RuleSeverity | null;
}) {
  const { primary, general, guide } = why;
  // No "Active" chip: a reason on a case comes from a rule that runs.
  return (
    <div className="flex flex-col gap-3" data-why={primary.rule.id}>
      <ToneBadge tone={toneOf(why, severity)} className="w-fit" />
      <div className="flex flex-col gap-0.5">
        <Typography variant="label">What it checks</Typography>
        <Typography variant="body-sm" className="break-words">
          {primary.rule.checks}
        </Typography>
      </div>
      <div className="flex flex-col gap-0.5">
        <Typography variant="label">What to do</Typography>
        <Typography variant="body-sm" className="break-words">
          {primary.rule.whatToDo}
        </Typography>
      </div>
      <ul className="flex flex-col gap-1 text-sm">
        <li>
          <Link to={explanationHref(primary)} className={LINK}>
            Open the full explanation
          </Link>
        </li>
        {general && (
          <li>
            <Link to={explanationHref(general)} className={LINK}>
              Rule for every country: {general.rule.title}
            </Link>
          </li>
        )}
        {guide && (
          <li>
            <Link to={`/rules/${guide.id}`} className={LINK}>
              Open the guide {guide.title}
            </Link>
          </li>
        )}
      </ul>
    </div>
  );
}

export interface WhyLinkProps {
  /** The registry rule id of the reason. */
  ruleId: string;
  /** The company of the case (entity code). */
  entity?: string | null;
  /** The severity of the reason on this case (the chip in the pop-up says the same). */
  severity?: RuleSeverity | null;
  className?: string;
}

export function WhyLink({ ruleId, entity, severity, className }: WhyLinkProps) {
  const why = whyFor(ruleId, entity);
  if (!why) {
    if (import.meta.env.DEV) {
      logger.warn("[rules] no explanation", { ruleId, entity });
    }
    return null;
  }
  return <WhyPopover why={why} severity={severity} className={className} />;
}

/**
 * "Why?" next to a line reason code that is not a registry rule (`photo_read`,
 * `scan_read`): the same pop-up, with the card of `REASON_CARDS`. Nothing for a code
 * without a card.
 */
export function ReasonWhyLink({
  reasonCode,
  className,
}: {
  reasonCode: string;
  className?: string;
}) {
  const why = whyForReason(reasonCode);
  return why ? <WhyPopover why={why} className={className} /> : null;
}

function WhyPopover({
  why,
  severity,
  className,
}: {
  why: WhyExplanation;
  severity?: RuleSeverity | null;
  className?: string;
}) {
  const { ruleId, entity } = why;
  const title = why.primary.rule.title;
  return (
    <Popover.Root
      onOpenChange={(open) => {
        if (open) {
          logger.debug("[rules] why", { ruleId, entity, card: why.primary.rule.id });
          track("case.why_open", { what: "rule" });
        }
      }}
    >
      <Popover.Trigger
        render={
          <Button
            variant="ghost"
            tone="neutral"
            size="sm"
            className={className}
            aria-label={`Why: ${title}`}
            data-why-link={ruleId}
          >
            Why?
          </Button>
        }
      />
      <Popover.Portal>
        <Popover.Positioner side="bottom" align="start" sideOffset={6}>
          <Popover.Popup className="max-w-[min(24rem,calc(100vw-2rem))]">
            <Popover.Header>
              <Popover.Title>{title}</Popover.Title>
              <Popover.CloseButton label="Close" />
            </Popover.Header>
            <Popover.Body>
              <WhyContent why={why} severity={severity} />
            </Popover.Body>
          </Popover.Popup>
        </Popover.Positioner>
      </Popover.Portal>
    </Popover.Root>
  );
}
